import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "@components";
import {Country, EventItem, FetchRaceByIdRequest, Lookups, Race, RaceScoring, ScoringForType, TOAST_STATE} from "@model";
import {LoaderService, RaceService, ToastService} from "@services";
import {required} from "@utility";
import {take} from "rxjs";

@Component({
  selector: 'app-race-details-additional-info',
  templateUrl: './race-details-additional-info.component.html',
  styleUrls: ['./race-details-additional-info.component.scss']
})
export class RaceDetailsAdditionalInfoComponent extends AbstractComponent implements OnInit {
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() scoringForTypes: Lookups[] = [];
  @Input() countries: Country[] = [];
  @Output() raceAdditionalInfoChange: EventEmitter<Race | undefined> = new EventEmitter<Race | undefined>();

  inputForm = new FormGroup({
    shortDescription: new FormControl(),
    shortDescriptionEng: new FormControl(),
    shortDescriptionIta: new FormControl(),
    shortDescriptionSlo: new FormControl(),
    shortDescriptionSr: new FormControl(),
    shortDescriptionBs: new FormControl(),
    registrationMessage: new FormControl(),
    registrationMessageEng: new FormControl(),
    registrationMessageIta: new FormControl(),
    registrationMessageSlo: new FormControl(),
    registrationMessageSr: new FormControl(),
    registrationMessageBs: new FormControl(),
    scoringDefinition: new FormControl(),
    scoringFor: new FormControl(),
    itraPoints: new FormControl(),
    mountainLevel: new FormControl(),
    utmbQualifier: new FormControl(),
    finisherLevel: new FormControl(),
    nameOne: new FormControl(),
    valueOne: new FormControl(),
  });

  constructor(
    public translocoService: TranslocoService,
    private raceService: RaceService,
    private toastService: ToastService,
    private loaderService: LoaderService,
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToScoringDefinitionValueChange();
    this.subscribeToScoringForValueChange();
    this.initForm()
    this.raceItemChanged();
  }

  private initForm() {
    this.inputForm.controls.scoringDefinition.addValidators([required]);
  }


  private subscribeToScoringDefinitionValueChange() {
    this.subscriptions.push(this.inputForm.controls.scoringDefinition.valueChanges.subscribe((scoringDefinition) => {
      if (scoringDefinition === true) {
        this.inputForm.controls.scoringFor.enable();
        this.inputForm.controls.scoringFor.addValidators([required]);
      } else {
        this.inputForm.controls.scoringFor.removeValidators([required]);
        this.inputForm.controls.scoringFor.setValue(undefined);
        this.inputForm.controls.scoringFor.disable();
      }
      this.inputForm.controls.scoringFor.markAsUntouched();
      this.inputForm.controls.scoringFor.updateValueAndValidity();
    }));
  }

  private subscribeToScoringForValueChange() {
    this.subscriptions.push(this.inputForm.controls.scoringFor.valueChanges.subscribe((scoringForValue) => {
      switch (scoringForValue) {
        case ScoringForType.ITRA:
          this.disableFormControl(this.inputForm.controls.valueOne);
          this.disableFormControl(this.inputForm.controls.nameOne);
          this.enableFormControl(this.inputForm.controls.itraPoints, true);
          this.enableFormControl(this.inputForm.controls.mountainLevel);
          this.enableFormControl(this.inputForm.controls.utmbQualifier);
          this.enableFormControl(this.inputForm.controls.finisherLevel);
          break;
        case ScoringForType.CONFIGURE:
          this.disableFormControl(this.inputForm.controls.itraPoints);
          this.disableFormControl(this.inputForm.controls.mountainLevel);
          this.disableFormControl(this.inputForm.controls.utmbQualifier);
          this.disableFormControl(this.inputForm.controls.finisherLevel);
          this.enableFormControl(this.inputForm.controls.valueOne, true);
          this.enableFormControl(this.inputForm.controls.nameOne, true);
          break;
        default:
          this.disableFormControl(this.inputForm.controls.itraPoints);
          this.disableFormControl(this.inputForm.controls.mountainLevel);
          this.disableFormControl(this.inputForm.controls.utmbQualifier);
          this.disableFormControl(this.inputForm.controls.finisherLevel);
          this.disableFormControl(this.inputForm.controls.valueOne);
          this.disableFormControl(this.inputForm.controls.nameOne);
          break;
      }
    }));
  }

  private disableFormControl(formControl: FormControl) {
    formControl.removeValidators([required]);
    formControl.setValue(undefined);
    formControl.disable();
  }

  private enableFormControl(formControl: FormControl, isRequired?: boolean) {
    formControl.enable();
    formControl.markAsUntouched();
    if (isRequired) {
      formControl.addValidators([required]);
    }
    formControl.updateValueAndValidity();
  }


  private raceItemChanged(): void {
    if (this.race) {
      this.inputForm.patchValue(this.race);
    }
    this.inputForm.controls.scoringDefinition.setValue(this.race?.raceScoring?.scoringDefinition ?? false);
    this.inputForm.controls.scoringFor.setValue(this.race?.raceScoring?.scoringFor ?? undefined);
    this.inputForm.controls.itraPoints.setValue(this.race?.raceScoring?.itraPoints ?? undefined);
    this.inputForm.controls.mountainLevel.setValue(this.race?.raceScoring?.mountainLevel ?? undefined);
    this.inputForm.controls.utmbQualifier.setValue(this.race?.raceScoring?.utmbQualifier ?? undefined);
    this.inputForm.controls.finisherLevel.setValue(this.race?.raceScoring?.finisherLevel ?? undefined);
    this.inputForm.controls.nameOne.setValue(this.race?.raceScoring?.nameOne ?? undefined);
    this.inputForm.controls.valueOne.setValue(this.race?.raceScoring?.valueOne ?? undefined);

  }

  private getAdditionalDetailsFields(): Race {
    this.checkValidators(this.inputForm);
    let raceDetails: Race = {...this.race};
    let raceScoring: RaceScoring = {...this.race?.raceScoring};
    raceDetails.shortDescription = this.inputForm.value.shortDescription;
    raceDetails.shortDescriptionEng = this.inputForm.value.shortDescriptionEng;
    raceDetails.shortDescriptionIta = this.inputForm.value.shortDescriptionIta;
    raceDetails.shortDescriptionSlo = this.inputForm.value.shortDescriptionSlo;
    raceDetails.shortDescriptionSr = this.inputForm.value.shortDescriptionSr;
    raceDetails.shortDescriptionBs = this.inputForm.value.shortDescriptionBs;

    raceDetails.registrationMessage = this.inputForm.value.registrationMessage;
    raceDetails.registrationMessageEng = this.inputForm.value.registrationMessageEng;
    raceDetails.registrationMessageIta = this.inputForm.value.registrationMessageIta;
    raceDetails.registrationMessageSlo = this.inputForm.value.registrationMessageSlo;
    raceDetails.registrationMessageSr = this.inputForm.value.registrationMessageSr;
    raceDetails.registrationMessageBs = this.inputForm.value.registrationMessageBs;

    raceScoring.scoringDefinition = this.inputForm.value.scoringDefinition;
    raceScoring.scoringFor = this.inputForm.value.scoringFor;
    raceScoring.itraPoints = this.inputForm.value.itraPoints;
    raceScoring.mountainLevel = this.inputForm.value.mountainLevel;
    raceScoring.utmbQualifier = this.inputForm.value.utmbQualifier;
    raceScoring.finisherLevel = this.inputForm.value.finisherLevel;
    raceScoring.nameOne = this.inputForm.value.nameOne;
    raceScoring.valueOne = this.inputForm.value.valueOne;

    if (!raceScoring.raceId) {
      raceScoring.raceId = raceDetails.id;
    }

    raceDetails.raceScoring = raceScoring;
    return raceDetails;
  }

  onCancel(): void {
    this.raceItemChanged()
  }

  onSave(): void {
    if (!this.inputForm.valid || this.disableEditing) {
      for (const control in this.inputForm.controls) {
        this.inputForm.get(control)?.markAsTouched();
      }
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.additional.data.error.form"))
      return;
    } else if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    } else {
      let raceDetails: Race = this.getAdditionalDetailsFields();
      this.loaderService.startLoading();
      this.raceService.updateRace(raceDetails, (raceId: number) => {
        this.raceService.findRaceByIdProtected(new FetchRaceByIdRequest(raceId), true)
          .pipe(take(1)).subscribe((race: Race | null) => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("app.common.success.race.edit"))
          this.raceAdditionalInfoChange.emit(race ?? undefined);
        })
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.common.server.error"))
      });
    }
  }
}
